import { OwnerPartnershipDiagram } from "./campaigns/OwnerPartnershipDiagram";
import { ExploreOpportunitiesDiagram } from "./campaigns/ExploreOpportunitiesDiagram";
import { ReactNode } from 'react';
import { Language } from '../types';

export type LocalizedString = string | Partial<Record<Language, string>>;
export type LocalizedReactNode = ReactNode | Partial<Record<Language, ReactNode>>;

export type CtaActionType = 'url' | 'pdf' | 'modal' | 'video' | 'custom';

export interface PromoCta {
  label: LocalizedString;
  type: CtaActionType;
  payload: string; // E.g., URL, video ID, pdf link
  target?: '_self' | '_blank';
}

export interface PromoTriggerConfig {
  delayMs?: number;
  scrollPercentage?: number;
  pagesViewed?: number;
  exitIntent?: boolean;
  manual?: boolean;
  frequencyDays?: number;
}

export interface PromoAudience {
  visitorType?: 'all' | 'new' | 'returning';
  devices?: ('desktop' | 'mobile' | 'tablet')[];
  referralSources?: string[];
  userSegments?: string[];
}

export interface PromoSlideAssetConfig {
  desktopImage?: string;
  mobileImage?: string;
  alt?: LocalizedString;
  version?: string;
  lastUpdated?: string;
}

export interface PromoSlide {
  // 9. Campaign Metadata
  id: string;
  name: string;
  category?: string;
  priority?: number;
  tags?: string[];

  // 6. Campaign Status
  status: 'enabled' | 'draft';
  
  // 5. Campaign Scheduling Fields
  startDate?: string; // ISO String
  endDate?: string;   // ISO String

  // 10. Target/Exclude Pages (URL-based activation)
  targetPages?: string[]; // e.g. ['/', '/models']
  excludePages?: string[]; // e.g. ['/contact']
  urlParams?: Record<string, string>; // e.g. { utm_campaign: 'summer' }

  // 1. Intelligent Trigger & 2. Trigger Rules
  triggers?: PromoTriggerConfig;

  // 8. Future Personalisation
  audience?: PromoAudience;

  // 8. Theme Support
  theme?: 'light' | 'dark' | 'custom';

  // 1. Responsive Image Support (Legacy)
  image?: string;
  mobileImage?: string;

  // 11. Asset Metadata (Versioned)
  assets?: PromoSlideAssetConfig;

  // Basic Content
  title?: LocalizedString;
  description?: LocalizedString;
  
  // 2. Rich Content Support
  richContent?: LocalizedReactNode;

  // 7. CTA Action Architecture
  ctas?: PromoCta[];
}

const BenefitList = ({ lang }: { lang: Language }) => {
  const benefits = {
    [Language.EN]: [
      "Work alongside an experienced project management team.",
      "Stay informed throughout construction.",
      "Clear responsibilities from day one.",
      "Greater visibility at every stage."
    ],
    [Language.ZH]: [
      "与经验丰富的项目管理团队并肩工作。",
      "在整个施工过程中保持信息畅通。",
      "从第一天起明确责任。",
      "每个阶段都有更高的透明度。"
    ],
    [Language.VI]: [
      "Làm việc cùng đội ngũ quản lý dự án giàu kinh nghiệm.",
      "Luôn được cập nhật thông tin trong suốt quá trình thi công.",
      "Trách nhiệm rõ ràng ngay từ ngày đầu.",
      "Tính minh bạch cao hơn ở mọi giai đoạn."
    ],
    [Language.HI]: [
      "एक अनुभवी परियोजना प्रबंधन टीम के साथ काम करें।",
      "निर्माण के दौरान सूचित रहें।",
      "पहले दिन से स्पष्ट जिम्मेदारियां।",
      "हर चरण में अधिक दृश्यता।"
    ]
  };
  
  const list = benefits[lang] || benefits[Language.EN];
  
  return (
    <ul className="space-y-3 mb-8">
      {list.map((b, i) => (
        <li key={i} className="flex items-start text-neutral-600">
          <svg className="w-4 h-4 text-neutral-900 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-sm font-medium">{b}</span>
        </li>
      ))}
    </ul>
  );
};

const OpportunityBenefitList = ({ lang }: { lang: Language }) => {
  const benefits = {
    [Language.EN]: [
      "Explore project opportunities",
      "Meet like-minded people",
      "Professionally facilitated introductions",
      "No public contact sharing"
    ],
    [Language.ZH]: [
      "探索项目合作与机遇",
      "结识志同道合的行业伙伴",
      "专业撮合与引荐服务",
      "隐私保护，不公开联系方式"
    ],
    [Language.VI]: [
      "Khám phá cơ hội dự án",
      "Gặp gỡ những đối tác cùng tầm nhìn",
      "Kết nối được hỗ trợ chuyên nghiệp",
      "Không chia sẻ thông tin liên hệ công khai"
    ],
    [Language.HI]: [
      "प्रोजेक्ट अवसरों का अन्वेषण करें",
      "समान विचारधारा वाले लोगों से मिलें",
      "पेशेवर रूप से सुगम परिचय",
      "कोई सार्वजनिक संपर्क साझाकरण नहीं"
    ]
  };

  const list = benefits[lang] || benefits[Language.EN];

  return (
    <ul className="space-y-3 mb-8">
      {list.map((b, i) => (
        <li key={i} className="flex items-start text-neutral-600">
          <svg className="w-4 h-4 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-sm font-medium">{b}</span>
        </li>
      ))}
    </ul>
  );
};

export const POPUP_VERSION = 'v1.3';

export const promoSlides: PromoSlide[] = [
  {
    id: 'op-slide-1-concept',
    name: 'Owner Partnership - Concept',
    category: 'Owner Partnership',
    priority: 1,
    status: 'enabled',
    theme: 'light',
    image: '/campaigns/owner-partnership/desktop-v1.webp',
    mobileImage: '/campaigns/owner-partnership/mobile-v1.webp',
    title: {
      en: 'Absolute Transparency. Total Control.',
      zh: '绝对透明。完全控制。',
      vi: 'Minh bạch tuyệt đối. Kiểm soát hoàn toàn.',
      hi: 'पूर्ण पारदर्शिता। पूर्ण नियंत्रण।'
    },
    description: {
      en: 'Discover a transparent delivery model where you retain complete control over your project funding.',
      zh: '探索一种透明的交付模式，让您对项目资金保留完全的控制权。',
      vi: 'Khám phá mô hình bàn giao minh bạch, nơi bạn giữ toàn quyền kiểm soát nguồn vốn dự án.',
      hi: 'एक पारदर्शी वितरण मॉडल की खोज करें जहां आप अपनी परियोजना निधि पर पूर्ण नियंत्रण बनाए रखते हैं।'
    },
    richContent: {
      en: <><BenefitList lang={Language.EN} /><OwnerPartnershipDiagram language={Language.EN} /></>,
      zh: <><BenefitList lang={Language.ZH} /><OwnerPartnershipDiagram language={Language.ZH} /></>,
      vi: <><BenefitList lang={Language.VI} /><OwnerPartnershipDiagram language={Language.VI} /></>,
      hi: <><BenefitList lang={Language.HI} /><OwnerPartnershipDiagram language={Language.HI} /></>,
    },
    ctas: [
      {
        label: {
          en: 'See How It Works',
          zh: '看看它是如何工作的',
          vi: 'Xem cách hoạt động',
          hi: 'देखें यह कैसे काम करता है'
        },
        type: 'url',
        payload: '?v=owner-partnership',
        target: '_self',
      }
    ]
  },
  {
    id: 'explore-opportunities-slide',
    name: 'Explore Opportunities',
    category: 'Ecosystem',
    priority: 1,
    status: 'enabled',
    theme: 'light',
    image: '/campaigns/explore-opportunities/desktop-v1.webp',
    mobileImage: '/campaigns/explore-opportunities/desktop-v1.webp',
    title: {
      en: 'EVERY GREAT PROJECT STARTS WITH THE RIGHT CONNECTION.',
      zh: '每一个伟大的项目，都始于正确的链接。',
      vi: 'MỌI DỰ ÁN TUYỆT VỜI ĐỀU BẮT ĐẦU TỪ SỰ KẾT NỐI PHÙ HỢP.',
      hi: 'हर बेहतरीन प्रोजेक्ट सही कनेक्शन से शुरू होता है।'
    },
    description: {
      en: 'Explore opportunities, discover potential collaborations, and connect with homeowners, investors, developers and industry professionals through a professionally managed introduction platform.',
      zh: '通过专业管理的引荐平台，探索项目机遇，发掘潜在合作，与业主、投资者、开发商及行业专业人士建立联系。',
      vi: 'Khám phá cơ hội, tìm kiếm đối tác tiềm năng và kết nối với chủ nhà, nhà đầu tư, nhà phát triển cùng các chuyên gia trong ngành thông qua nền tảng giới thiệu được quản lý chuyên nghiệp.',
      hi: 'अवसरों का अन्वेषण करें, संभावित सहयोग की खोज करें, और एक पेशेवर रूप से प्रबंधित प्लेटफ़ॉर्म के माध्यम से गृहस्वामियों, निवेशकों, डेवलपर्स और उद्योग पेशेवरों के साथ जुड़ें।'
    },
    richContent: {
      en: <><OpportunityBenefitList lang={Language.EN} /><ExploreOpportunitiesDiagram language={Language.EN} /></>,
      zh: <><OpportunityBenefitList lang={Language.ZH} /><ExploreOpportunitiesDiagram language={Language.ZH} /></>,
      vi: <><OpportunityBenefitList lang={Language.VI} /><ExploreOpportunitiesDiagram language={Language.VI} /></>,
      hi: <><OpportunityBenefitList lang={Language.HI} /><ExploreOpportunitiesDiagram language={Language.HI} /></>,
    },
    ctas: [
      {
        label: {
          en: 'Explore Opportunities',
          zh: '探索合作机遇',
          vi: 'Khám phá Cơ hội',
          hi: 'अवसरों का अन्वेषण करें'
        },
        type: 'url',
        payload: '?v=explore-opportunities',
        target: '_self',
      }
    ]
  }
];
